import React, { useState, useEffect } from 'react';
import { FaChalkboardTeacher, FaPlus, FaEdit, FaTrash, FaEnvelope, FaPhone, FaLock, FaTimes, FaCheck } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { validateForm, isValidEmail, isValidPhoneNumber } from '../../utils/validators';

/**
 * Teacher management component for school administrators
 * Allows adding, editing, and managing teachers
 */
const TeacherManagement = () => {
  const { showSuccess, showError } = useToast();

  // Teachers state
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [schoolId, setSchoolId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subjects: [],
    qualifications: '',
    notes: '',
    password: '',
    confirmPassword: '',
    createAccount: true
  });
  
  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Available subjects
  const availableSubjects = [
    'Mathematics', 'English', 'Science', 'Social Studies', 'History', 
    'Geography', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 
    'Physical Education', 'Art', 'Music', 'Languages', 'Religious Education'
  ];

  // Fetch teachers on component mount
  useEffect(() => {
    fetchTeachers();
  }, []);

  // Fetch teachers from API
  const fetchTeachers = async () => {
    try {
      setLoading(true);
      
      // Get school ID using a helper function
      const schoolId = await getSchoolId();
      setSchoolId(schoolId);
      
      // Use the school ID to fetch teachers
      const response = await api.get(`/schools/${schoolId}/teachers`);
      
      // Map the server phoneNumber field to our local phone field for consistency
      const teachersData = response.data.data || [];
      const mappedTeachers = teachersData.map(teacher => ({
        ...teacher,
        phone: teacher.phoneNumber // Map phoneNumber to phone for our local state
      }));
      
      setTeachers(mappedTeachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      showError('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get school ID
  const getSchoolId = async () => {
    // First, try to get from dashboard
    try {
      const schoolResponse = await api.get('/schools/dashboard');
      console.log('School dashboard response:', schoolResponse.data);
      
      const schoolData = schoolResponse.data.data;
      
      if (schoolData && schoolData.schoolInfo && schoolData.schoolInfo._id) {
        return schoolData.schoolInfo._id;
      } else if (schoolData && schoolData.school && schoolData.school._id) {
        return schoolData.school._id;
      }
    } catch (error) {
      console.error('Error getting school ID from dashboard:', error);
    }
    
    // If not found in dashboard, try user data
    try {
      const userResponse = await api.get('/auth/me');
      const userData = userResponse.data.data;
      if (userData && userData.school) {
        return userData.school;
      }
    } catch (error) {
      console.error('Error getting school ID from user data:', error);
    }
    
    throw new Error('Could not determine school ID');
  };

  // Filter teachers based on search term
  const filteredTeachers = searchTerm
    ? teachers.filter(
        teacher =>
          teacher.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          teacher.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (teacher.subjects && teacher.subjects.some(subject => 
            subject.toLowerCase().includes(searchTerm.toLowerCase())
          ))
      )
    : teachers;

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'subjects') {
      // Handle subjects as a multi-select
      const subjectValue = value;
      setFormData(prev => {
        if (prev.subjects.includes(subjectValue)) {
          return {
            ...prev,
            subjects: prev.subjects.filter(subject => subject !== subjectValue)
          };
        } else {
          return {
            ...prev,
            subjects: [...prev.subjects, subjectValue]
          };
        }
      });
    } else {
      // Handle regular inputs
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear the error for this field if there was one
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Open add form
  const handleAddNew = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subjects: [],
      qualifications: '',
      notes: '',
      password: '',
      confirmPassword: '',
      createAccount: true
    });
    setFormMode('add');
    setShowForm(true);
    setFormErrors({});
  };

  // Open edit form
  const handleEdit = (teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      firstName: teacher.firstName || '',
      lastName: teacher.lastName || '',
      email: teacher.email || '',
      phone: teacher.phoneNumber || teacher.phone || '',
      subjects: teacher.subjects || [],
      qualifications: teacher.qualifications || '',
      notes: teacher.notes || '',
      password: '',
      confirmPassword: '',
      createAccount: false
    });
    setFormMode('edit');
    setShowForm(true);
    setFormErrors({});
  };

  // Open delete confirmation
  const handleDeleteClick = (teacher) => {
    setSelectedTeacher(teacher);
    setShowDeleteModal(true);
  };

  // Delete teacher
  const handleDeleteConfirm = async () => {
    if (!selectedTeacher) return;
    
    try {
      await api.delete(`/schools/${schoolId}/teachers/${selectedTeacher._id}`);
      showSuccess('Teacher deleted successfully');
      setShowDeleteModal(false);
      fetchTeachers();
    } catch (error) {
      console.error('Error deleting teacher:', error);
      showError('Failed to delete teacher');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Prepare teacher data
    const teacherData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phone,
      subjects: formData.subjects,
      qualifications: formData.qualifications,
      notes: formData.notes
    };
    
    // Add password if adding new teacher and createAccount is checked
    if (formMode === 'add' && formData.createAccount) {
      teacherData.password = formData.password;
      // Ensure role is included so user is created as a teacher
      teacherData.role = 'teacher';
    }
    
    try {
      if (formMode === 'add') {
        await api.post(`/schools/${schoolId}/teachers`, teacherData);
        showSuccess('Teacher added successfully');
        
        if (formData.createAccount) {
          showSuccess('Login credentials have been created. Please inform the teacher to use their email address and the provided password to sign in.');
        }
      } else {
        try {
          // First try the school admin endpoint
          await api.put(`/schools/${schoolId}/teachers/${selectedTeacher._id}`, teacherData);
        } catch (error) {
          // If that fails due to permissions, try the teacher profile endpoint
          if (error.response?.status === 403) {
            await api.put(`/teachers/profile/${selectedTeacher._id}`, teacherData);
          } else {
            // If it's another type of error, re-throw it
            throw error;
          }
        }
        showSuccess('Teacher updated successfully');
      }
      
      setShowForm(false);
      fetchTeachers();
    } catch (error) {
      console.error('Error saving teacher:', error);
      showError(error.response?.data?.message || 'Failed to save teacher');
    }
  };

  // Toggle teacher active status
  const handleToggleStatus = async (teacher) => {
    try {
      const updatedStatus = !teacher.isActive;
      await api.put(`/schools/${schoolId}/teachers/${teacher._id}/status`, {
        isActive: updatedStatus
      });
      showSuccess(`Teacher ${updatedStatus ? 'activated' : 'deactivated'} successfully`);
      fetchTeachers();
    } catch (error) {
      console.error('Error updating teacher status:', error);
      showError('Failed to update teacher status');
    }
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (optional field)
    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    // Password validation for new accounts
    if (formData.createAccount && formMode === 'add') {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
      
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    // Update formErrors state
    setFormErrors(errors);
    
    // Return true if no errors, false otherwise
    return Object.keys(errors).length === 0;
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaChalkboardTeacher className="mr-2 text-primary" /> Teacher Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your school's teaching staff
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add New Teacher
          </button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search teachers by name, email, or subject..."
            className="form-input pl-10 py-2 w-full"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Teachers List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {filteredTeachers.length === 0 ? (
            <div className="p-8 text-center">
              <FaChalkboardTeacher className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                {searchTerm ? 'No teachers match your search' : 'No Teachers Added Yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Add teachers to your school to get started'}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleAddNew}
                  className="btn-primary flex items-center mx-auto"
                >
                  <FaPlus className="mr-2" /> Add Teacher
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teacher
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subjects
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="font-medium text-primary">
                              {teacher.firstName[0]}{teacher.lastName[0]}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {teacher.firstName} {teacher.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {teacher.qualifications}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <FaEnvelope className="mr-2 text-gray-400" /> {teacher.email}
                        </div>
                        {teacher.phone && (
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <FaPhone className="mr-2 text-gray-400" /> {teacher.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {teacher.subjects && teacher.subjects.map((subject, index) => (
                            <span 
                              key={index} 
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {subject}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-2.5 w-2.5 rounded-full mr-2 ${teacher.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <span>{teacher.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(teacher)}
                          className="text-primary hover:text-primary-600 mr-3"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(teacher)}
                          className={`mr-3 ${teacher.isActive ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`}
                          title={teacher.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {teacher.isActive ? <FaTimes /> : <FaCheck />}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(teacher)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit Teacher Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {formMode === 'add' ? 'Add New Teacher' : 'Edit Teacher'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-4">Personal Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">First Name*</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`form-input w-full ${formErrors.firstName ? 'border-red-500' : ''}`}
                      />
                      {formErrors.firstName && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Last Name*</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`form-input w-full ${formErrors.lastName ? 'border-red-500' : ''}`}
                      />
                      {formErrors.lastName && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Email Address*</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`form-input w-full ${formErrors.email ? 'border-red-500' : ''}`}
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`form-input w-full ${formErrors.phone ? 'border-red-500' : ''}`}
                      placeholder="e.g. 0712345678"
                    />
                    {formErrors.phone && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Qualifications</label>
                    <input
                      type="text"
                      name="qualifications"
                      value={formData.qualifications}
                      onChange={handleChange}
                      className="form-input w-full"
                      placeholder="e.g. B.Ed, M.Ed, etc."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      className="form-textarea w-full"
                      placeholder="Additional information about the teacher"
                    ></textarea>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-4">Teaching & Account</h4>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Subjects</label>
                    <div className="border border-gray-300 rounded-md p-3 h-48 overflow-y-auto">
                      {availableSubjects.map((subject) => (
                        <label key={subject} className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            name="subjects"
                            value={subject}
                            checked={formData.subjects.includes(subject)}
                            onChange={handleChange}
                            className="form-checkbox mr-2"
                          />
                          <span>{subject}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Account Creation Section */}
                  {formMode === 'add' && (
                    <div className="border border-gray-200 rounded-md p-4 mb-4">
                      <div className="flex items-center mb-4">
                        <input
                          type="checkbox"
                          id="createAccount"
                          name="createAccount"
                          checked={formData.createAccount}
                          onChange={handleChange}
                          className="form-checkbox mr-2"
                        />
                        <label htmlFor="createAccount" className="font-medium">
                          Create teacher account
                        </label>
                      </div>
                      
                      {formData.createAccount && (
                        <>
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Password*</label>
                            <div className="relative">
                              <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`form-input w-full pl-8 ${formErrors.password ? 'border-red-500' : ''}`}
                                placeholder="Minimum 8 characters"
                              />
                              <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            </div>
                            {formErrors.password && (
                              <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                            )}
                          </div>
                          
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Confirm Password*</label>
                            <div className="relative">
                              <input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={`form-input w-full pl-8 ${formErrors.confirmPassword ? 'border-red-500' : ''}`}
                              />
                              <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            </div>
                            {formErrors.confirmPassword && (
                              <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-500">
                            An invitation email will be sent to the teacher with login instructions.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {formMode === 'add' ? 'Add Teacher' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="p-6">
          <p className="mb-4">
            Are you sure you want to delete {selectedTeacher ? `${selectedTeacher.firstName} ${selectedTeacher.lastName}` : ''}?
            This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TeacherManagement;
